/**
 * @fileoverview Column definitions for the Day-One generated documents table.
 *
 * Pure presentational. Wired up by `DayOneDataTable.tsx`.
 *
 * Columns:
 *   - Select  (bulk-action checkbox)
 *   - Document (template name + nested generated attachments) — sortable
 *   - Phase   (job-status tags from the template) — sortable, filterable
 *   - Contact (recipient name + role badge, click opens the contact
 *     detail panel) — sortable, filterable, groupable
 *   - Workflow (firm taskStatuses — send/response tracking lives here;
 *     blank on nested attachments, which follow their pack's status)
 *   - Actions (Generate draft email on the pack only → "Ready for review
 *     in mailbox" while a draft is pending → "Sent via email" after Mail
 *     approval/send; nested attachments have no email button)
 *   - Row tools (preview / download / delete — icon column)
 *
 * Row callbacks are dispatched via `table.options.meta` so columns stay
 * free of parent prop drilling.
 */

import { type ColumnDef, type SortingFn } from "@tanstack/react-table";
import {
  ChevronRight,
  Download,
  Eye,
  Paperclip,
  Sparkles,
  Trash2,
} from "lucide-react";
import { Badge, Button, Checkbox } from "@/ui/primitives";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays/alert-dialog";
import { cn } from "@/utils/misc";
import type { DayOnePrecedentRow } from "./types";
import { WorkflowStatusSelect } from "@/components/tasks/WorkflowStatusSelect";
import { SortableHeader, ContactCell } from "./columns.cells";
import { getMeta } from "./columns.meta";
import {
  contactTypeGroupValue,
  contactTypeRank,
  phaseGroupValue,
  phaseRank,
} from "./columns.helpers";
import { JOB_STATUS_LABELS, JOB_STATUS_VARIANTS } from "@/lib/job-statuses";

/**
 * A pack reads as a sequence — cover letter, then Part A, then Part B — so
 * nested attachments must stay in the order the parent template attaches
 * them. TanStack applies the active sort at every level of the row model,
 * which would otherwise re-order sub-rows alphabetically by whichever
 * column the practitioner sorted on. Returning 0 for two attachment
 * siblings leaves them to the (stable) sort's original order, which is the
 * attach order `nestGeneratedAttachments` built, and is direction-agnostic
 * so descending sorts don't reverse a pack either.
 */
function keepAttachmentOrder(
  compare: SortingFn<DayOnePrecedentRow>,
): SortingFn<DayOnePrecedentRow> {
  return (a, b, columnId) => {
    if (a.original.isAttachment && b.original.isAttachment) return 0;
    return compare(a, b, columnId);
  };
}

export const dayOneColumns: Array<ColumnDef<DayOnePrecedentRow>> = [
  {
    id: "select",
    enableSorting: false,
    enableGrouping: false,
    header: ({ table }) => {
      const allSelected = table.getIsAllRowsSelected();
      const someSelected = table.getIsSomeRowsSelected();
      return (
        <Checkbox
          checked={allSelected ? true : someSelected ? "indeterminate" : false}
          onCheckedChange={(checked) =>
            table.toggleAllRowsSelected(checked === true)
          }
          aria-label="Select all"
        />
      );
    },
    cell: ({ row }) =>
      // Annexure rows can't be bulk-selected: they always download inside
      // their pack, so selecting them alongside the pack produced duplicate
      // files in bulk downloads (MOR-971).
      row.original.isAttachment ? null : (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(checked) => row.toggleSelected(checked === true)}
          aria-label="Select row"
          onClick={(e) => e.stopPropagation()}
          className="mt-0.5"
        />
      ),
  },
  {
    id: "name",
    accessorKey: "name",
    header: ({ column }) => <SortableHeader column={column} label="Document" />,
    sortingFn: keepAttachmentOrder((a, b) =>
      a.original.name.localeCompare(b.original.name),
    ),
    cell: ({ row }) => {
      const isAttachment = row.original.isAttachment;
      const fileAttachment = row.original.fileAttachment;
      const nestedCount = row.original.subRows?.length ?? 0;
      const canExpandPack = !isAttachment && nestedCount > 0;
      const attachmentCount = Math.max(
        row.original.attachmentCount,
        nestedCount,
      );
      const fileMissing =
        !!fileAttachment &&
        (fileAttachment.status !== "resolved" || !fileAttachment.downloadUrl);
      return (
        <div
          className={cn(
            "flex min-w-0 items-start gap-1.5",
            isAttachment && "pl-4",
          )}
        >
          {canExpandPack ? (
            <button
              type="button"
              aria-label={
                row.getIsExpanded()
                  ? "Hide attachments"
                  : `Show ${attachmentCount} attachment${attachmentCount === 1 ? "" : "s"}`
              }
              onClick={(e) => {
                e.stopPropagation();
                row.toggleExpanded();
              }}
              className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <ChevronRight
                className={cn(
                  "size-3.5 transition-transform",
                  row.getIsExpanded() && "rotate-90",
                )}
              />
            </button>
          ) : isAttachment ? (
            <Paperclip className="mt-0.5 size-4 shrink-0 p-0.5 text-muted-foreground" />
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-1.5">
              <span
                className={cn(
                  "truncate text-sm font-medium",
                  isAttachment ? "text-muted-foreground" : "text-primary/80",
                )}
              >
                {row.original.name}
              </span>
              {isAttachment && (
                <Badge
                  variant="secondary"
                  className="shrink-0 text-[10px]"
                  title={
                    row.original.attachedToName
                      ? `Attachment of "${row.original.attachedToName}" — included automatically when the pack is downloaded or emailed`
                      : "Included automatically when its pack is downloaded or emailed"
                  }
                >
                  Attachment
                </Badge>
              )}
              {fileMissing && (
                <Badge variant="destructive" className="shrink-0 text-[10px]">
                  Missing
                </Badge>
              )}
            </div>
            {fileAttachment && fileAttachment.fileName && (
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                {fileAttachment.slotName}
              </div>
            )}
            {!isAttachment && attachmentCount > 0 && !row.getIsExpanded() && (
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                {attachmentCount} attachment
                {attachmentCount === 1 ? "" : "s"}
              </div>
            )}
          </div>
        </div>
      );
    },
  },
  {
    // Phase (job-status) tags derived live from the template's `js:` tags.
    // A document can belong to several phases (e.g. Day One + Active).
    id: "phase",
    accessorFn: (row) => phaseGroupValue(row),
    header: ({ column }) => <SortableHeader column={column} label="Phase" />,
    sortingFn: keepAttachmentOrder(
      (a, b) =>
        phaseRank(phaseGroupValue(a.original)) -
        phaseRank(phaseGroupValue(b.original)),
    ),
    filterFn: (row, _id, value) => {
      if (!Array.isArray(value) || value.length === 0) return true;
      const tags = row.original.phaseTags ?? [];
      if (tags.length === 0) return value.includes("__none__");
      return tags.some((t) => value.includes(t));
    },
    getGroupingValue: (row) => phaseGroupValue(row),
    cell: ({ row }) => {
      const tags = row.original.phaseTags ?? [];
      if (tags.length === 0) {
        return <span className="text-xs text-muted-foreground/40">—</span>;
      }
      return (
        <div className="flex flex-wrap gap-1">
          {tags.map((t) => (
            <Badge
              key={t}
              variant={JOB_STATUS_VARIANTS[t] ?? "outline"}
              className="text-[10px]"
            >
              {JOB_STATUS_LABELS[t] ?? t}
            </Badge>
          ))}
        </div>
      );
    },
  },
  {
    id: "contact",
    accessorFn: (row) => row.contactDisplayName ?? "",
    header: ({ column }) => <SortableHeader column={column} label="Contact" />,
    sortingFn: keepAttachmentOrder((a, b) => {
      const aHas = Boolean(a.original.jobContactId || a.original.firmContactId);
      const bHas = Boolean(b.original.jobContactId || b.original.firmContactId);
      // Surface fanned-out (contact-linked) rows above the "—" rows when
      // sorting ascending so the practitioner can scan recipient packs first.
      if (aHas !== bHas) return aHas ? -1 : 1;
      return (a.original.contactDisplayName ?? "").localeCompare(
        b.original.contactDisplayName ?? "",
      );
    }),
    filterFn: (row, _id, value) => {
      if (!Array.isArray(value) || value.length === 0) return true;
      const kind =
        row.original.contactRoleKind ?? row.original.firmContactCategory;
      // `__none__` matches non-fanned-out documents so the practitioner
      // can isolate the unscoped templates from contact packs.
      if (!kind) return value.includes("__none__");
      return value.includes(kind);
    },
    getGroupingValue: (row) =>
      row.contactDisplayName
        ? `${row.contactRoleKind ?? row.firmContactCategory ?? "__"}::${row.contactDisplayName}`
        : "__none__",
    cell: ContactCell,
  },
  {
    // Hidden column (see `columnVisibility` in DayOneDataTable) that exists
    // so "Group: Contact type" can gather every row of one role/category —
    // e.g. all banks — for bulk generation (MOR-840). Its sortingFn orders
    // the groups canonically when the grouping is active.
    id: "contactType",
    accessorFn: contactTypeGroupValue,
    sortingFn: (a, b) =>
      contactTypeRank(contactTypeGroupValue(a.original)) -
      contactTypeRank(contactTypeGroupValue(b.original)),
    header: () => null,
    cell: () => null,
  },
  {
    id: "workflowStatus",
    accessorKey: "workflowStatusId",
    enableSorting: false,
    enableGrouping: false,
    header: () => (
      <span className="px-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Workflow
      </span>
    ),
    cell: ({ row, table }) => {
      const meta = getMeta(table);
      if (!meta.orgId) return null;
      // Attachments move through the workflow with their parent pack, so a
      // per-attachment status would be noise the practitioner can't act on.
      if (row.original.isAttachment) return null;
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <WorkflowStatusSelect
            orgId={meta.orgId}
            value={row.original.workflowStatusId ?? null}
            onChange={(statusId) =>
              meta.onWorkflowStatusChange(row.original._id, statusId)
            }
          />
        </div>
      );
    },
  },
  {
    // Display header "Actions" — id avoids clash with the icon toolbar column.
    id: "morganAction",
    enableSorting: false,
    enableGrouping: false,
    header: () => (
      <span className="px-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Actions
      </span>
    ),
    cell: ({ row, table }) => {
      // Packs send their attachments with the cover letter — nested
      // rows are not independently emailed (one button per pack).
      if (row.original.isAttachment) {
        return <span className="text-xs text-muted-foreground/40">—</span>;
      }
      const meta = getMeta(table);
      const canDraft = !!meta.onSendWithMorgan;
      const awaitingApproval = meta.pendingDraftPrecedentIds.has(
        row.original._id,
      );
      const isSentViaEmail =
        row.original.sendStatus === "sent" ||
        row.original.sendStatus === "response_received";

      // Pending draft wins over a prior send (re-generate flow).
      if (awaitingApproval) {
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <span
              className="whitespace-nowrap rounded-md border border-amber-600/20 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium leading-none text-amber-700 dark:text-amber-400"
              title="Morgan drafted the email — open Mail → For Review to approve and send"
            >
              Ready for review in mailbox
            </span>
          </div>
        );
      }

      if (isSentViaEmail) {
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <span
              className="whitespace-nowrap rounded-md border border-emerald-600/20 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium leading-none text-emerald-700 dark:text-emerald-400"
              title="Approved and sent from Mail"
            >
              Sent via email
            </span>
          </div>
        );
      }

      return (
        <div onClick={(e) => e.stopPropagation()}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canDraft}
            title={
              canDraft
                ? "Morgan drafts a cover email for approval in Mail → For Review"
                : "Enable the job mailbox on the Mail page first"
            }
            onClick={() => meta.onSendWithMorgan?.(row.original._id)}
            className="h-8 gap-1.5 px-2.5 text-xs font-medium text-primary hover:bg-primary/5 hover:text-primary"
          >
            <Sparkles className="size-3.5 shrink-0" />
            <span className="truncate">Generate draft email</span>
          </Button>
        </div>
      );
    },
  },
  {
    id: "actions",
    enableSorting: false,
    enableGrouping: false,
    header: () => <span className="sr-only">Actions</span>,
    cell: ({ row, table }) => {
      const meta = getMeta(table);
      const fileAttachment = row.original.fileAttachment;
      if (fileAttachment) {
        // Uploaded / job-document files aren't precedents: open and
        // download straight from storage, and there's nothing to delete
        // (the slot is managed via the pack's "Edit attachments").
        const url = fileAttachment.downloadUrl;
        return (
          <div
            className="flex items-center justify-end gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              disabled={!url}
              onClick={() => meta.onPreview(row.original)}
              className="rounded p-1.5 text-primary/40 transition hover:text-primary/60 disabled:cursor-not-allowed disabled:opacity-40"
              title={url ? "Open" : "File not available"}
            >
              <Eye className="size-3.5" />
            </button>
            {url ? (
              <a
                href={url}
                download={fileAttachment.fileName ?? fileAttachment.slotName}
                target="_blank"
                rel="noreferrer"
                className="rounded p-1.5 text-primary/40 transition hover:text-primary/60"
                title="Download"
              >
                <Download className="size-3.5" />
              </a>
            ) : (
              <span
                className="rounded p-1.5 text-primary/40 opacity-40"
                title="File not available"
              >
                <Download className="size-3.5" />
              </span>
            )}
          </div>
        );
      }
      return (
        <div
          className="flex items-center justify-end gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => meta.onPreview(row.original)}
            className="rounded p-1.5 text-primary/40 transition hover:text-primary/60"
            title="Preview"
          >
            <Eye className="size-3.5" />
          </button>
          {meta.renderDownloadMenu(row.original)}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                className="rounded p-1.5 text-primary/40 transition hover:bg-destructive/10 hover:text-destructive"
                title="Delete"
              >
                <Trash2 className="size-3.5" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete precedent?</AlertDialogTitle>
                <AlertDialogDescription>
                  &quot;{row.original.name}&quot; will be permanently deleted.
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() =>
                    meta.onDelete(row.original._id, row.original.name)
                  }
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      );
    },
  },
];

/** Selectable groupings for the "Group by" picker. */
export type DayOneGrouping =
  "none" | "contact" | "contactType" | "phase" | "document";
